import { ElementType, ReactNode } from "react";
import { clsx } from "clsx";
import { Description, Field, Label } from "@headlessui/react";
import Tooltip from "@/common/Tooltip";

/**
 * One row of a `FormSection`: what the setting is on the left, the control on
 * the right. Every control shares one measure, so their edges line up down the
 * page.
 *
 * The label is the control's label, so clicking it focuses the control (or
 * flips it, for a switch).
 */
export function FormRow({
    label,
    icon: Icon,
    badge,
    description,
    aside,
    asideCaption,
    asideColumn = false,
    lockedReason,
    stacked = false,
    children,
}: {
    /** None for a field that needs no name, such as a dialog's only one. */
    label?: ReactNode;
    /**
     * A small picture of the setting before its label, to find a row by. Give
     * every row of a card one or none: the text of a row without one would
     * start further left than its neighbours'.
     */
    icon?: ElementType;
    /** What applies to this row only, beside its label: "Needs Lens". */
    badge?: ReactNode;
    /**
     * The row's one explanation: a sentence or two on what the setting does,
     * and a link for the rest. There is deliberately no second slot under the
     * control; what an unset control falls back to goes in its placeholder.
     */
    description?: ReactNode;
    /**
     * Beside the control and outside its measure, so the control stays as wide
     * as the ones above and below it: a clear or a copy icon, or a column the
     * rows of a card share, such as how much each one counts.
     */
    aside?: ReactNode;
    /**
     * Names the column the aside is in. Pass it on the first row of the column
     * only; that row makes room for it above the aside.
     */
    asideCaption?: string;
    /**
     * The aside is a column of controls the card's rows share, not an icon
     * that belongs to the field, so it stands further from the field.
     */
    asideColumn?: boolean;
    /**
     * Why this reader cannot change the setting, shown on hover over its
     * control. A disabled control with nothing on screen to explain it is a
     * dead end.
     */
    lockedReason?: string | null;
    /**
     * Label over control at every width, and no rule or padding of its own:
     * the row of a dialog or a side panel, which is too narrow for two
     * columns. The order is the same, name and explanation first.
     *
     * `"beside"` is for a lone switch: it stays on the right of its label, as
     * it is in a card, instead of dropping under the explanation.
     */
    stacked?: boolean | "beside";
    /** The control. */
    children: ReactNode;
}) {
    const beside = stacked === "beside";
    const control = (
        <div
            className={clsx(
                "flex flex-col gap-3",
                beside ? "shrink-0" : "w-full",
                // A dialog sets the measure itself.
                !stacked && "max-w-lg",
            )}
        >
            {children}
        </div>
    );

    return (
        <Field
            className={clsx(
                // Stacked, a label sits closer to its control than a row does
                // to the next one, so each field reads as one thing.
                "flex gap-x-8",
                stacked ? "gap-y-2" : "gap-y-3",
                beside ? "flex-row items-start justify-between" : "flex-col",
                !stacked && "py-6 last:pb-0 xl:flex-row",
                !stacked && asideCaption && "xl:pt-11",
            )}
        >
            {/* A fixed width, not a share of the card: on a wide screen a
                share leaves the label a long way from its control. The padding
                centres the label on the first line of a control, which is
                taller than a line of text. */}
            <div
                className={clsx(
                    "flex min-w-0 gap-2",
                    // Wider only where there is room for it: at the narrower
                    // width a wider label column squeezes the control.
                    !stacked && "xl:w-72 xl:shrink-0 xl:pt-1.5 2xl:w-80",
                    !label && !description && "hidden",
                )}
            >
                {/* Quieter than the card's icon, which is the one accent;
                    these only help the eye down the column. As wide as that
                    icon and centred under it, so labels start where the title
                    does. */}
                {Icon && (
                    <span
                        aria-hidden={"true"}
                        className={"mt-1 flex w-5 shrink-0 justify-center"}
                    >
                        <Icon
                            className={
                                "size-4 text-zinc-400 dark:text-zinc-500"
                            }
                        />
                    </span>
                )}
                <div className={"min-w-0"}>
                    <div className={"flex flex-wrap items-center gap-x-2"}>
                        {label && (
                            <Label
                                className={
                                    "block select-none text-base/6 font-medium text-default sm:text-sm/6"
                                }
                            >
                                {label}
                            </Label>
                        )}
                        {badge}
                    </div>
                    {description && (
                        <Description
                            // Pretty, so a narrow column never leaves the last
                            // word of a sentence on a line of its own.
                            className={
                                "text-pretty text-base/6 text-subtle sm:text-sm/5"
                            }
                        >
                            {description}
                        </Description>
                    )}
                </div>
            </div>
            <div
                className={clsx(
                    "flex min-w-0 items-start gap-2",
                    !beside && "flex-1",
                    // Stacked under its label, the caption needs the room the
                    // row's own padding gives it when they sit side by side.
                    asideCaption && "mt-5 xl:mt-0",
                )}
            >
                {lockedReason ? (
                    <Tooltip
                        asChild
                        content={lockedReason}
                        placement={"top-start"}
                    >
                        {control}
                    </Tooltip>
                ) : (
                    control
                )}
                {aside && (
                    <div
                        className={clsx(
                            "relative flex h-11 shrink-0 items-center sm:h-9",
                            asideColumn && "ml-4",
                        )}
                    >
                        {asideCaption && (
                            <span
                                aria-hidden={"true"}
                                className={
                                    "absolute bottom-full left-0 mb-1 whitespace-nowrap text-xs font-medium text-subtle"
                                }
                            >
                                {asideCaption}
                            </span>
                        )}
                        {aside}
                    </div>
                )}
            </div>
        </Field>
    );
}
